import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFor, auth, pathToNs } from "@/lib/api";

const AuthCtx = createContext(null);

// Four parallel role slots — each mapped 1:1 to a namespace in lib/api.js.
const NS_CUSTOMER     = "customer";
const NS_SUPER_ADMIN  = "super_admin";
const NS_STORE_ADMIN  = "store_admin";
const NS_DELIVERY     = "delivery";

const roleLabelForNs = {
  [NS_CUSTOMER]:    "CUSTOMER",
  [NS_SUPER_ADMIN]: "SUPER_ADMIN",
  [NS_STORE_ADMIN]: "STORE_ADMIN",
  [NS_DELIVERY]:    "DELIVERY_PARTNER",
};

// AuthProvider MUST live inside <BrowserRouter> — it depends on useLocation
// to derive the active role for the current tab.
export function AuthProvider({ children }) {
  const location = useLocation();
  const activeNs = useMemo(() => pathToNs(location.pathname), [location.pathname]);

  // Four parallel session slots — kept in sync with localStorage so a
  // background tab that just logged into a different role is immediately
  // visible when the user switches back.
  const [customerUser,   setCustomerUser]   = useState(() => auth.getUserFor("CUSTOMER"));
  const [superAdminUser, setSuperAdminUser] = useState(() => auth.getUserFor("SUPER_ADMIN"));
  const [storeAdminUser, setStoreAdminUser] = useState(() => auth.getUserFor("STORE_ADMIN"));
  const [deliveryUser,   setDeliveryUser]   = useState(() => auth.getUserFor("DELIVERY_PARTNER"));
  const [booting, setBooting] = useState(true);

  const setForNs = useCallback((ns, u) => {
    if (ns === NS_SUPER_ADMIN) return setSuperAdminUser(u);
    if (ns === NS_STORE_ADMIN) return setStoreAdminUser(u);
    if (ns === NS_DELIVERY)    return setDeliveryUser(u);
    return setCustomerUser(u);
  }, []);

  // Boot: refresh /auth/me for every namespace that has a live token.
  useEffect(() => {
    let cancelled = false;
    const refresh = async (ns) => {
      const role = roleLabelForNs[ns];
      if (!auth.getAccessFor(role)) return;
      try {
        const { data } = await apiFor(role).get("/auth/me");
        if (cancelled) return;
        const merged = { ...(auth.getUserFor(role) || {}), ...data };
        auth.setUser(merged);
        setForNs(ns, merged);

        // For a SUPER_ADMIN session with no store bound yet, auto-pick one so
        // legacy pages that need X-Store-Id keep working. STORE_ADMIN always
        // has a store_id on their profile.
        if (ns === NS_SUPER_ADMIN && !auth.getStoreIdFor(role)) {
          if (data.store_id) {
            auth.setStoreId(data.store_id, role);
          } else {
            try {
              const s = await apiFor(role).get("/stores?limit=10");
              const first = (s.data.items || [])[0];
              if (first?.id) auth.setStoreId(first.id, role);
            } catch { /* noop */ }
          }
        }
      } catch {
        // Bad/expired token — quietly drop this slot.
        auth.clearFor(role);
        setForNs(ns, null);
      }
    };
    (async () => {
      await Promise.all([
        refresh(NS_CUSTOMER),
        refresh(NS_SUPER_ADMIN),
        refresh(NS_STORE_ADMIN),
        refresh(NS_DELIVERY),
      ]);
      if (!cancelled) setBooting(false);
    })();
    return () => { cancelled = true; };
  }, [setForNs]);

  // Cross-tab reactive sync: if another tab logs in/out on any role, mirror
  // it here. localStorage `storage` event fires only for OTHER tabs.
  useEffect(() => {
    const handler = (e) => {
      if (!e.key) return;
      if (e.key === "customer_user")         setCustomerUser(auth.getUserFor("CUSTOMER"));
      else if (e.key === "super_admin_user") setSuperAdminUser(auth.getUserFor("SUPER_ADMIN"));
      else if (e.key === "store_admin_user") setStoreAdminUser(auth.getUserFor("STORE_ADMIN"));
      else if (e.key === "delivery_user")    setDeliveryUser(auth.getUserFor("DELIVERY_PARTNER"));
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const activeUser = activeNs === NS_SUPER_ADMIN ? superAdminUser
                   : activeNs === NS_STORE_ADMIN ? storeAdminUser
                   : activeNs === NS_DELIVERY    ? deliveryUser
                   : customerUser;

  const login = useCallback(async (email, password) => {
    // Any client works for /auth/login (no bearer required) — use customer's.
    const { data } = await apiFor("CUSTOMER").post("/auth/login", { email, password });
    // setSession writes to the correct namespace AND stamps the per-tab admin
    // marker if applicable — so this tab locks onto this role.
    auth.setSession(data);
    const role = data.user?.role;
    const ns = role === "CUSTOMER" ? NS_CUSTOMER
             : (role === "DELIVERY_PARTNER" || role === "DELIVERY_MANAGER") ? NS_DELIVERY
             : role === "SUPER_ADMIN" ? NS_SUPER_ADMIN
             : NS_STORE_ADMIN;
    setForNs(ns, data.user);

    // Super-admin without a specific store → auto-select first available.
    if (ns === NS_SUPER_ADMIN && !data.user.store_id && !auth.getStoreIdFor("SUPER_ADMIN")) {
      try {
        const s = await apiFor("SUPER_ADMIN").get("/stores?limit=10");
        const first = (s.data.items || [])[0];
        if (first?.id) auth.setStoreId(first.id, "SUPER_ADMIN");
      } catch { /* noop */ }
    }
    return data.user;
  }, [setForNs]);

  // Sprint — Super Admin impersonation. Snapshot the SA session in a shadow
  // key, then write the store-owner tokens into the store_admin namespace.
  // `endImpersonation()` restores SA and wipes the impersonated store_admin
  // session.
  const impersonate = useCallback(async (storeId) => {
    if (!storeId) throw new Error("storeId required");
    const { data } = await apiFor("SUPER_ADMIN").post(`/stores/${storeId}/impersonate`);
    const shadow = {
      access:  localStorage.getItem("super_admin_access_token"),
      refresh: localStorage.getItem("super_admin_refresh_token"),
      user:    localStorage.getItem("super_admin_user"),
      store:   localStorage.getItem("super_admin_store_id"),
    };
    localStorage.setItem("super_admin_shadow", JSON.stringify(shadow));
    // Swap in the store-owner session under the store_admin namespace.
    localStorage.setItem("store_admin_access_token",  data.tokens.access_token);
    localStorage.setItem("store_admin_refresh_token", data.tokens.refresh_token);
    localStorage.setItem("store_admin_user",          JSON.stringify(data.user));
    if (data.user.store_id) localStorage.setItem("store_admin_store_id", data.user.store_id);
    auth.markTabAs("STORE_ADMIN");
    setStoreAdminUser(data.user);
    return data.user;
  }, []);

  const endImpersonation = useCallback(() => {
    const raw = localStorage.getItem("super_admin_shadow");
    if (!raw) return false;
    let shadow;
    try { shadow = JSON.parse(raw); } catch { shadow = null; }
    if (!shadow) return false;
    if (shadow.access)  localStorage.setItem("super_admin_access_token",  shadow.access);
    if (shadow.refresh) localStorage.setItem("super_admin_refresh_token", shadow.refresh);
    if (shadow.user)    localStorage.setItem("super_admin_user",          shadow.user);
    if (shadow.store)   localStorage.setItem("super_admin_store_id",      shadow.store);
    localStorage.removeItem("super_admin_shadow");
    // Wipe the impersonated store_admin session.
    ["store_admin_access_token", "store_admin_refresh_token", "store_admin_user", "store_admin_store_id"]
      .forEach((k) => localStorage.removeItem(k));
    auth.markTabAs("SUPER_ADMIN");
    try { setSuperAdminUser(JSON.parse(shadow.user || "null")); } catch { /* noop */ }
    setStoreAdminUser(null);
    return true;
  }, []);

  const isImpersonating = typeof window !== "undefined" && !!localStorage.getItem("super_admin_shadow");

  const logout = useCallback(async () => {
    const ns = activeNs;
    const role = roleLabelForNs[ns];
    try { await apiFor(role).post("/auth/logout"); } catch { /* noop */ }
    auth.clearFor(role);
    setForNs(ns, null);
    window.location.href = "/login";
  }, [activeNs, setForNs]);

  const setUser = useCallback((u) => {
    // Update the currently active tab's slot. Persist to localStorage too so
    // hydrations stay consistent.
    setForNs(activeNs, u);
    if (u) auth.setUser(u);
  }, [activeNs, setForNs]);

  const value = { user: activeUser, booting, login, logout, setUser, activeRole: activeNs, impersonate, endImpersonation, isImpersonating };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

// Role helpers (unchanged public surface)
export const ROLES = {
  SUPER_ADMIN: "SUPER_ADMIN",
  STORE_ADMIN: "STORE_ADMIN",
  STORE_OWNER: "STORE_OWNER",
  STORE_MANAGER: "STORE_MANAGER",
  CASHIER: "CASHIER",
  PACKER: "PACKER",
  DELIVERY_MANAGER: "DELIVERY_MANAGER",
  DELIVERY_PARTNER: "DELIVERY_PARTNER",
  CUSTOMER: "CUSTOMER",
};

export const STAFF_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.STORE_ADMIN,
  ROLES.STORE_OWNER,
  ROLES.STORE_MANAGER,
  ROLES.CASHIER,
  ROLES.PACKER,
  ROLES.DELIVERY_MANAGER,
];

// Roles that behave as "store admin" — either name grants full store management.
export const STORE_ADMIN_ROLES = [ROLES.STORE_ADMIN, ROLES.STORE_OWNER];

export const isStaff = (u) => !!u && STAFF_ROLES.includes(u.role);
export const isSuperAdmin  = (u) => !!u && u.role === ROLES.SUPER_ADMIN;
export const isStoreAdmin  = (u) => !!u && STORE_ADMIN_ROLES.includes(u.role);
export const canPOS  = (u) => !!u && [ROLES.STORE_ADMIN, ROLES.STORE_OWNER, ROLES.STORE_MANAGER, ROLES.CASHIER].includes(u.role);
